import { useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileUp, Users, Building2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  new: { label: "New", className: "bg-[hsl(var(--halo-teal))]/15 text-[hsl(var(--halo-teal))] border-transparent" },
  duplicate_email: { label: "Possible duplicate", className: "bg-[hsl(var(--studio-danger))]/12 text-[hsl(var(--studio-danger))] border-transparent" },
  same_name: { label: "Same name", className: "bg-[hsl(var(--studio-warning)/0.22)] text-[hsl(34_42%_34%)] dark:text-[hsl(34_50%_78%)] border-transparent" },
  unusable: { label: "No name", className: "" },
};

const ACTIONS = [
  { value: "new", label: "Import as new" },
  { value: "update", label: "Update existing" },
  { value: "skip", label: "Skip" },
];

/** Upload → review every row → commit. Nothing is saved until Import is pressed. */
export const ImportWizard = ({ onDone }) => {
  const fileRef = useRef();
  const [analysis, setAnalysis] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");

  const analyze = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API_BASE}/import/analyze`, { method: "POST", body: fd, credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not read that file");
      setAnalysis(data);
      setDecisions(Object.fromEntries(data.rows.map((r) => [r.index, {
        action: r.suggested_action, record_type: r.record_type, preferred_name: "",
      }])));
      toast.success(`Read ${data.total} rows from your ${data.format.toUpperCase()}`);
    } catch (e) { toast.error(e.message || "Import failed"); }
    finally { setBusy(false); }
  };

  const setRow = (index, patch) =>
    setDecisions((s) => ({ ...s, [index]: { ...s[index], ...patch } }));

  const bulk = (action) =>
    setDecisions((s) => Object.fromEntries(Object.entries(s).map(([k, v]) => [k, { ...v, action }])));

  const commit = async () => {
    setBusy(true);
    try {
      const rows = analysis.rows.filter((r) => r.usable).map((r) => ({
        payload: r.payload,
        record_type: decisions[r.index]?.record_type || r.record_type,
        action: decisions[r.index]?.action || "skip",
        existing_id: r.existing?.id || null,
        preferred_name: decisions[r.index]?.preferred_name || null,
      }));
      const { data } = await api.post("/import/commit", { rows });
      toast.success(`Added ${data.created} · Updated ${data.updated} · Skipped ${data.skipped}`);
      setAnalysis(null);
      setDecisions({});
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  const rows = (analysis?.rows || []).filter((r) => {
    if (filter === "all") return true;
    if (filter === "flagged") return r.match_status !== "new";
    return r.record_type === filter;
  });

  return (
    <Card className="studio-card" data-testid="import-wizard">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Upload className="h-4 w-4" /> Import contacts
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!analysis ? (
          <>
            <p className="text-sm text-muted-foreground">
              People and companies in one go — CSV, TSV, TXT, Excel (.xlsx), JSON, vCard (.vcf) or PDF.
              Column names are matched for you, so Google, Outlook, Apple and Spot'd exports all work.
              You'll see every row and decide what happens before anything is saved.
            </p>
            <input ref={fileRef} type="file" className="hidden" data-testid="import-file-input"
                   accept=".csv,.tsv,.txt,.xlsx,.json,.vcf,.vcard,.pdf"
                   onChange={(e) => analyze(e.target.files?.[0])} />
            <Button data-testid="import-choose-file" disabled={busy} onClick={() => fileRef.current?.click()}>
              <FileUp className="mr-2 h-4 w-4" /> {busy ? "Reading…" : "Choose a file"}
            </Button>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2" data-testid="import-summary">
              <Badge variant="secondary" className="font-normal">{analysis.total} rows · {analysis.format.toUpperCase()}</Badge>
              <Badge className={`font-normal ${STATUS_META.new.className}`}>{analysis.counts.new} new</Badge>
              {analysis.counts.duplicate_email > 0 && (
                <Badge className={`font-normal ${STATUS_META.duplicate_email.className}`}>{analysis.counts.duplicate_email} matching email</Badge>
              )}
              {analysis.counts.same_name > 0 && (
                <Badge className={`font-normal ${STATUS_META.same_name.className}`}>{analysis.counts.same_name} same name</Badge>
              )}
              <Badge variant="outline" className="font-normal"><Users className="mr-1 h-3 w-3" />{analysis.counts.person} people</Badge>
              <Badge variant="outline" className="font-normal"><Building2 className="mr-1 h-3 w-3" />{analysis.counts.company} companies</Badge>
              {analysis.counts.unusable > 0 && (
                <Badge variant="outline" className="font-normal">{analysis.counts.unusable} without a name (ignored)</Badge>
              )}
            </div>

            {analysis.unmapped_headers.length > 0 && (
              <p className="flex items-start gap-2 text-xs text-muted-foreground" data-testid="import-unmapped">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Columns I couldn't place (their data is ignored): {analysis.unmapped_headers.join(", ")}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-2 border-y border-border py-2">
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="h-8 w-[170px]" data-testid="import-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All rows</SelectItem>
                  <SelectItem value="flagged">Needs a decision</SelectItem>
                  <SelectItem value="person">People only</SelectItem>
                  <SelectItem value="company">Companies only</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">Set all to:</span>
              {ACTIONS.map((a) => (
                <Button key={a.value} size="sm" variant="ghost" data-testid={`import-bulk-${a.value}`}
                        onClick={() => bulk(a.value)}>{a.label}</Button>
              ))}
            </div>

            <div className="max-h-[420px] overflow-y-auto rounded-lg border border-border" data-testid="import-review-table">
              {rows.map((r) => {
                const d = decisions[r.index] || {};
                const meta = STATUS_META[r.match_status] || {};
                return (
                  <div key={r.index} className="flex flex-wrap items-center gap-2 border-b border-border p-3 last:border-0"
                       data-testid={`import-row-${r.index}`}>
                    <div className="min-w-[180px] flex-1">
                      <div className="truncate text-sm font-medium">{r.name || "—"}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {[r.email, r.role, r.location].filter(Boolean).join(" · ") || "no other details"}
                      </div>
                      {r.existing && (
                        <div className="mt-1 text-[11px] text-muted-foreground">
                          Existing: {r.existing.name}{r.existing.email ? ` · ${r.existing.email}` : ""}
                        </div>
                      )}
                    </div>
                    <Badge className={`font-normal ${meta.className}`} variant={meta.className ? undefined : "outline"}>
                      {meta.label || r.match_status}
                    </Badge>
                    <Select value={d.record_type || r.record_type}
                            onValueChange={(v) => setRow(r.index, { record_type: v })}>
                      <SelectTrigger className="h-8 w-[112px]" data-testid={`import-type-${r.index}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="person">Person</SelectItem>
                        <SelectItem value="company">Company</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={d.action || "skip"} onValueChange={(v) => setRow(r.index, { action: v })}>
                      <SelectTrigger className="h-8 w-[150px]" data-testid={`import-action-${r.index}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ACTIONS.map((a) => (
                          <SelectItem key={a.value} value={a.value}
                                      disabled={a.value === "update" && !r.existing}>{a.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {r.match_status === "same_name" && (d.action || "new") === "new" && (
                      <Input className="h-8 w-[170px]" placeholder="Preferred name (optional)"
                             data-testid={`import-preferred-${r.index}`}
                             value={d.preferred_name || ""}
                             onChange={(e) => setRow(r.index, { preferred_name: e.target.value })} />
                    )}
                  </div>
                );
              })}
              {rows.length === 0 && <p className="p-4 text-sm text-muted-foreground">Nothing matches that filter.</p>}
            </div>

            <div className="flex gap-2">
              <Button data-testid="import-commit" disabled={busy} onClick={commit}>
                <CheckCircle2 className="mr-2 h-4 w-4" /> {busy ? "Importing…" : "Import contacts"}
              </Button>
              <Button variant="outline" data-testid="import-cancel" onClick={() => { setAnalysis(null); setDecisions({}); }}>
                Start over
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
